import React from "react";
import { MdDelete, MdDownload, MdEdit } from "react-icons/md";

import { PaziaLoader } from "components/atoms";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { appendFileToBrowser, toMoneyFormat } from "utils";
import { formatDate } from "utils";
import { COURSE_FINANCES, CourseFinance, CourseFinanceType, CreateCourseFinanseRequest, courseFinancesApi } from "api";
import { useToaster } from "utils";
import { EditCourseFinanceModal } from "./EditModal/EditCourseFinanceModal";
import { AdminResourceDeleteModal } from "components/molecules";
import { useDeleteResourceActions } from "utils/store";
import { useCourseFinancesByCourseId } from "api/course-finances";
import { queryClient } from "main";
import { QUERIES_KEYS } from "queries";

export const CourseFinancesTable = ({ courseId }: { courseId: number }) => {
  const { setDeleteResource } = useDeleteResourceActions();
  const { showError, showSuccess } = useToaster();
  const [editModalFinanceId, setEditModalFinanceId] = React.useState<number | null>(null);
  const { isLoading, invalidate, courseFinances } = useCourseFinancesByCourseId(courseId);

  const onDownload = async (courseFinanceId: number, fileName: string) => {
    try {
      const response = await courseFinancesApi.downloadInvoice(courseId, courseFinanceId);
      appendFileToBrowser(response.data, fileName);
    } catch (error) {
      showError("Wystąpił błąd podczas pobierania faktury");
      console.error(error);
    }
  };

  const onEditSubmit = async (data: CreateCourseFinanseRequest) => {
    if (!data.id) {
      console.error("No id in data");
      showError("Wystąpił błąd podczas edycji płatności");
      return;
    }
    try {
      await courseFinancesApi.update(courseId, data.id, data);
      setEditModalFinanceId(null);
      showSuccess("Płatność została zaktualizowana");
    } catch (error) {
      showError("Wystąpił błąd podczas edycji płatności");
      console.error(error);
    } finally {
      refreshCourseFinances();
    }
  };

  const refreshCourseFinances = () => {
    invalidate();

    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSE_FINANCES(courseId),
    });
  };

  const columns: TanTableColumn<CourseFinance>[] = [
    {
      id: "name",
      header: "Nazwa",
      accessorKey: "name",
    },
    {
      id: "value",
      header: "Wartość",
      accessorKey: "value",
      cell: (row) => toMoneyFormat(row.getValue<number>()),
    },
    {
      id: "date",
      header: "Data",
      accessorKey: "date",
      cell: (row) => formatDate(row.getValue<Date>()),
    },
    {
      id: "type",
      header: "Typ",
      accessorKey: "type",
      cell: (row) => statuses[row.getValue<CourseFinanceType>()],
    },
    {
      id: "invoice",
      header: "Faktura",
      accessorKey: "hasInvoice",
      cell: (row) =>
        row.getValue<boolean>() &&
        row.row.original.invoice && (
          <span
            className="flex cursor-pointer items-center gap-2"
            onClick={() => onDownload(row.row.original.id, row.row.original.invoice!)}
          >
            <MdDownload /> Pobierz
          </span>
        ),
    },
    {
      id: "actions",
      header: "Akcje",
      accessorKey: "id",
      cell: (row) => {
        if (row.row.original.type != COURSE_FINANCES.OTHER) return null;
        return (
          <div className="flex gap-2">
            <MdEdit
              onClick={(e) => {
                e.stopPropagation();
                setEditModalFinanceId(row.getValue<number>());
              }}
              size={24}
              className="cursor-pointer whitespace-nowrap text-right hover:text-blue-500"
            />
            <MdDelete
              size={24}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteResource(row.getValue<number>());
              }}
              className="cursor-pointer whitespace-nowrap text-right hover:text-red-500"
            />
          </div>
        );
      },
    },
  ];
  if (isLoading) return <PaziaLoader />;

  return (
    <>
      <TanTableClient data={courseFinances} columns={columns} />
      <EditCourseFinanceModal
        isOpen={!!editModalFinanceId}
        onCloseRequest={() => setEditModalFinanceId(null)}
        courseId={courseId}
        courseFinanceId={editModalFinanceId}
        onSubmit={onEditSubmit}
      />
      <AdminResourceDeleteModal
        title="Usuń płatność"
        description="Jesteś pewien że chcesz usunąć płatność?"
        onSuccessDelete={() => refreshCourseFinances()}
        deleteAction={(x) => courseFinancesApi.delete(courseId, x)}
      />
    </>
  );
};

const statuses: Record<CourseFinanceType, React.JSX.Element> = {
  [COURSE_FINANCES.REGISTRATION]: <span className="font-semibold text-blue-500">Rejestracja na kurs</span>,
  [COURSE_FINANCES.EXAM]: <span className="font-semibold text-green-500">Opłata za kurs</span>,
  [COURSE_FINANCES.OTHER]: <span className="font-semibold text-gray-500">Inne</span>,
  [COURSE_FINANCES.RETAINED_REVENUE]: <span className="font-semibold text-amber-600">Zatrzymany przychód</span>,
  [COURSE_FINANCES.TRAINER_COST]: <span className="font-semibold text-rose-500">Koszt trenera</span>,
  [COURSE_FINANCES.REFUND]: <span className="font-semibold text-purple-600">Zwrot</span>,
};
